import { AsyncPipe, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faDownload } from '@fortawesome/pro-solid-svg-icons';
import {
  AaNgModule,
  ActivityTypeTextPipe,
  CSVBuilder,
  DownloadService,
  ISTPersonActivity,
  PersonNamePipe,
} from 'aa-ng';
import { AaNgBootstrapModule, ITableColumn } from 'aa-ng-bootstrap';
import { BehaviorSubject, combineLatest, filter, take, tap } from 'rxjs';
import { IHiddenFields } from 'src/app/models';

import {
  IActivityTypeLookupType,
  IActivityYearLookupType,
  IMultiSelectItem,
  IStPersonActivityExtended,
} from '../../models';
import {
  IActivitySearchFilterCounts,
  IActivitySearchFilters,
} from '../../models/activity-search';
import {
  ActivityCollaboratorsService,
  LoadingIndicatorService,
  MAX_SCHOLARS_TO_DISPLAY,
} from '../../services';
import { BaseDirective } from '../base.component';
import { PaginationMenuComponent } from '../pagination-menu/pagination-menu.component';
import { SearchBarComponent } from '../search-bar/search-bar.component';
import { ActivityTableFilterComponent } from './activity-table-filter.component';

@Component({
  selector: 'app-activity-table',
  imports: [
    AaNgBootstrapModule,
    AaNgModule,
    ActivityTableFilterComponent,
    AsyncPipe,
    FaIconComponent,
    NgClass,
    NgStyle,
    NgTemplateOutlet,
    PaginationMenuComponent,
    SearchBarComponent,
  ],
  templateUrl: './activity-table.component.html',
  styleUrl: './activity-table.component.scss',
})
export class ActivityTableComponent
  extends BaseDirective
  implements AfterViewInit, OnInit
{
  private activityTypeTextPipe = inject(ActivityTypeTextPipe);
  private downloadService = inject(DownloadService);
  private loadingIndicatorService = inject(LoadingIndicatorService);
  private personNamePipe = inject(PersonNamePipe);
  private activityCollaboratorsService = inject(ActivityCollaboratorsService);

  _activities: IStPersonActivityExtended[];
  _filteredActivities$ = new BehaviorSubject<IStPersonActivityExtended[]>([]);

  currentPage = 1;
  currentPageSize: number = 10;
  activityCountOnPage = 0;

  activityQueryText: string;

  filteredActivityTypes = new Set<number>();
  filteredActivityYears = new Set<number>();
  multiSelectActivityTypes: IMultiSelectItem<IActivityTypeLookupType>[] = [];
  multiSelectActivityYears: IMultiSelectItem<IActivityYearLookupType>[] = [];

  _columns$ = new BehaviorSubject<ITableColumn[]>([]);
  _configuredColumns$ = new BehaviorSubject<ITableColumn[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _sortedRowsFromTable$ = new BehaviorSubject<any>([]);

  forceMapPeople = false;

  /** Shared with the fetch, which asks for one more than this to know if there is a next page. */
  maxScholarsToDisplay = MAX_SCHOLARS_TO_DISPLAY;

  faDownload = faDownload;

  @Input() set activities(data: ISTPersonActivity[]) {
    this.currentPage = 1;
    if (data) {
      const extendedData: IStPersonActivityExtended[] = data.map((x) => ({
        ...x,
        show: false,
        displayId: x.id.toString() + '_' + this.generateRandomId(),
      }));
      this._activities = extendedData;

      this._filteredActivities$.next(extendedData);
      const startIndex = (this.currentPage - 1) * this.currentPageSize;
      const endIndex = startIndex + this.currentPageSize;
      const activitiesInRange = this._filteredActivities$.value.slice(
        startIndex,
        endIndex,
      );
      this.activityCountOnPage = Math.min(
        activitiesInRange.length,
        this.currentPageSize,
      );
      this.activityQueryText = '';
    }
  }

  @Input() set columns(data: ITableColumn[]) {
    this._columns$.next(data);
  }

  @Input() hiddenFields: IHiddenFields;
  @Input() hasPagination: boolean;

  get columns() {
    return this._columns$.value;
  }

  // Setting this to 'true' will override client-side filtering logic and configuration
  @Input() useServerSideFilters = false;
  // Following are for server-side filtering
  @Input() activitySearchFilterCounts: IActivitySearchFilterCounts;
  @Input() showDownloadButton = false;

  @Output() searchQueryChange = new EventEmitter<string>();
  @Output() filtersChange = new EventEmitter<IActivitySearchFilters>();

  constructor() {
    super();
    this._subscriptions.push(
      combineLatest([this._columns$])
        .pipe(
          tap(([columns]) => {
            this.configureColumns(columns);
          }),
        )
        .subscribe(),
    );
  }

  ngOnInit(): void {
    if (!this.useServerSideFilters) {
      this._subscriptions.push(
        this._filteredActivities$
          .pipe(
            filter((activities) => !!activities?.length),
            tap(() => {
              if (
                this.filteredActivityTypes.size === 0 &&
                this.filteredActivityYears.size === 0
              )
                this.configureSelectionLists();
            }),
          )
          .subscribe(),
      );
    }
  }

  ngAfterViewInit(): void {
    this._sortedRowsFromTable$
      .pipe(
        take(1),
        filter(
          (sortedActivities) =>
            !!sortedActivities && sortedActivities.length > 0,
        ),
        tap((sortedActivities) => {
          this._activities.sort((a, b) => {
            return sortedActivities.indexOf(a) - sortedActivities.indexOf(b);
          });
          this._filteredActivities$.next(this._activities);
        }),
      )
      .subscribe();
  }

  /**
   * The inline scholars for one row, fetched when the row asks for them.
   *
   * Only the first page: the row displays at most MAX_SCHOLARS_TO_DISPLAY and the rest live behind the
   * modal, so a hyperauthored work never pulls its whole author list to render ten names.
   */
  showPeople(activity: ISTPersonActivity) {
    this.loadingIndicatorService.forceLoadingState(true);
    this._subscriptions.push(
      this.activityCollaboratorsService.fetchFirstPage(activity).subscribe({
        next: (resultSet) => {
          const row = this._filteredActivities$.value.find(
            (a) =>
              a.id === activity.id &&
              a.activityTypeId === activity.activityTypeId,
          );

          if (row) {
            row.contributors = this.activityCollaboratorsService.toContributors(
              resultSet?.results,
            );
          }

          this.forceMapPeople = !this.forceMapPeople;
          this.loadingIndicatorService.forceLoadingState(false);
        },
        error: (err) => {
          console.error('showPeople error', err);
          this.loadingIndicatorService.forceLoadingState(false);
        },
      }),
    );
  }

  /** The full, server-paged collaborator list. */
  showAllCollaboratorsModal(activity: ISTPersonActivity) {
    this.activityCollaboratorsService.openModal(activity);
  }

  // REDO: legacy any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rowOrdersSorted(rows: any) {
    this._sortedRowsFromTable$.next(rows);
  }

  activityTypeFilterSelected(
    activityTypes: IMultiSelectItem<IActivityTypeLookupType>[],
  ) {
    const activityTypeIds = activityTypes.map((type) => type.data.id);

    if (activityTypeIds.length === 1) {
      if (this.filteredActivityTypes.has(activityTypeIds[0])) {
        this.filteredActivityTypes.delete(activityTypeIds[0]);
      } else {
        this.filteredActivityTypes.add(activityTypeIds[0]);
      }
    } else {
      if (activityTypeIds.some((id) => this.filteredActivityTypes.has(id))) {
        activityTypeIds.forEach((id) => this.filteredActivityTypes.delete(id));
      } else {
        activityTypeIds.forEach((id) => this.filteredActivityTypes.add(id));
      }
    }
  }

  activityYearFilterSelected(
    activityYears: IMultiSelectItem<IActivityYearLookupType>[],
  ) {
    const activityYearIds = activityYears.map((year) => year.data.name);

    if (activityYearIds.length === 1) {
      if (this.filteredActivityYears.has(activityYearIds[0])) {
        this.filteredActivityYears.delete(activityYearIds[0]);
      } else {
        this.filteredActivityYears.add(activityYearIds[0]);
      }
    } else {
      if (activityYearIds.some((id) => this.filteredActivityYears.has(id))) {
        activityYearIds.forEach((id) => this.filteredActivityYears.delete(id));
      } else {
        activityYearIds.forEach((id) => this.filteredActivityYears.add(id));
      }
    }
  }

  setSearchWithinResultsQuery(query: string) {
    if (this.useServerSideFilters) {
      if (query?.length > 2) {
        this.searchQueryChange.emit(query);
      } else {
        this.searchQueryChange.emit('');
      }
    } else {
      this.updateRowsWithFilters(query);
    }
  }

  updateRowsWithFilters(
    query?: string,
    multiSelectItems?: {
      activityTypes: IMultiSelectItem<IActivityTypeLookupType>[];
      years: IMultiSelectItem<IActivityYearLookupType>[];
    },
  ) {
    if (this.useServerSideFilters) {
      if (multiSelectItems) {
        this.filtersChange.emit({
          activityTypeIds: multiSelectItems.activityTypes
            .filter((x) => x.selected)
            .map((x) => x.data.id),
          years: multiSelectItems.years
            .filter((x) => x.selected)
            .map((x) => x.data.id),
        });
      }
    } else {
      let allActivities = this._activities;

      if (this.filteredActivityYears.size > 0) {
        allActivities = allActivities.filter((activity) =>
          this.filteredActivityYears.has(activity.activityYear),
        );
      }

      if (this.filteredActivityTypes.size > 0) {
        allActivities = allActivities.filter((activity) =>
          this.filteredActivityTypes.has(activity.activityTypeId),
        );
      }

      if (query !== undefined) {
        this.activityQueryText = (query || '').trim();
      }

      if (this.activityQueryText && this.activityQueryText != '') {
        allActivities = allActivities.filter((a) => {
          for (const value of Object.values(a)) {
            if (
              String(value)
                .toLowerCase()
                .includes(this.activityQueryText.toLowerCase())
            ) {
              return true;
            }
          }
          return false;
        });
      }

      this._filteredActivities$.next(allActivities);
    }
  }

  resetFilter() {
    this.filteredActivityYears = new Set<number>();
    this.filteredActivityTypes = new Set<number>();

    this.updateRowsWithFilters();
  }

  download() {
    const columnHeaders = ['Title', 'Work Id', 'Work Type', 'Year', 'Scholars'];
    const dataProperties: ((activity: ISTPersonActivity) => string)[] = [
      (item) => item.title ?? '',
      (item) => item.id.toString(),
      (item) => this.activityTypeTextPipe.transform(item.activityTypeId),
      (item) => (item.activityYear ? item.activityYear.toString() : ''),
      (item) =>
        item.collaboratorPersons
          ?.map((v) => this.personNamePipe.transform(v, true))
          .join(' | ') ?? '',
    ];

    this.downloadService.downloadByBuilder(
      new CSVBuilder<ISTPersonActivity>(
        'Related Works',
        columnHeaders,
        this._activities,
        dataProperties,
      ),
    );
  }

  private configureColumns(columns: ITableColumn[]) {
    const newColumns = [...columns];
    if (newColumns.length) {
      this._configuredColumns$.next(newColumns);
    }
  }

  private generateRandomId() {
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
  }

  private configureSelectionLists() {
    const uniqueYears = new Set<number>();

    this.multiSelectActivityYears = this._filteredActivities$.value
      .filter((option) => {
        if (uniqueYears.has(option.activityYear)) {
          return false;
        } else {
          uniqueYears.add(option.activityYear);
          return true;
        }
      })
      .map((option: ISTPersonActivity, index: number) => ({
        name: option.activityYear?.toString(),
        data: {
          id: index,
          name: option.activityYear,
        },
        selected: this.filteredActivityYears.has(option.activityYear),
      }))
      .sort((a, b) => b.data.name - a.data.name);

    const uniqueActivityTypes = new Set<number>();

    this.multiSelectActivityTypes = this._filteredActivities$.value
      .filter((option) => {
        if (uniqueActivityTypes.has(option.activityTypeId)) {
          return false;
        } else {
          uniqueActivityTypes.add(option.activityTypeId);
          return true;
        }
      })
      .map((option: ISTPersonActivity) => {
        const activityTypeName = this.activityTypeTextPipe.transform(
          option.activityTypeId,
        );

        return {
          name: activityTypeName,
          data: {
            id: option.activityTypeId,
            name: activityTypeName,
          },
          selected: this.filteredActivityTypes.has(option.activityTypeId),
        };
      })
      .sort((a, b) => a.data.name.localeCompare(b.data.name));
  }
}
